/**
 * A period-over-period change, coloured by whether it is good news.
 *
 * **Direction is not sentiment, and on a cost product they are usually
 * opposite.** Every dashboard kit colours "+27.9%" green because it was drawn
 * for revenue. Here the dominant metric is spend, where a rise is the bad
 * outcome and a fall is the win — so a chip that reads direction alone tells
 * the reader the opposite of the truth on the page they look at most.
 *
 * `sentiment` therefore defaults to `lower-is-better`. Anything measuring
 * savings, coverage or utilisation must opt into `higher-is-better` explicitly,
 * and `neutral` exists for counts, where up and down carry no verdict.
 *
 * A zero change is rendered as an em dash rather than a green 0%: "did not
 * move" is a different statement from "moved favourably by nothing", and a
 * coloured zero reads as the latter. A missing baseline is also an em dash,
 * uncoloured — absence is not zero — with the reason on hover, so a strip of
 * five tiles does not read "no baseline · no baseline · no baseline".
 */

import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";

import { Hint } from "@/components/ui/hint";
import { cn } from "@/lib/utils";

export type DeltaSentiment =
  | "lower-is-better"
  | "higher-is-better"
  | "neutral";

export interface DeltaChipProps {
  /**
   * The change as a **fraction**, not a percentage: `0.279` is +27.9%.
   *
   * `null` means there is nothing to compare against — a first period, or a
   * window that reaches past a provider's retention ceiling. Rendered as an
   * explicit absence rather than as zero, because "no baseline" and "no change"
   * are different facts and only one of them is reassuring.
   */
  value: number | null;
  sentiment?: DeltaSentiment;
  /** e.g. "vs last month". Kept to a few words; omitted on dense rows. */
  caption?: string;
  className?: string;
}

/** Below this, a change is noise: providers restate cents for days afterwards. */
const FLAT_THRESHOLD = 0.0005;

const NO_BASELINE = "No earlier period to compare against";

export function DeltaChip({
  value,
  sentiment = "lower-is-better",
  caption,
  className,
}: DeltaChipProps) {
  if (value === null || !Number.isFinite(value)) {
    // The reason is the design system's hover text; the label says it to a
    // screen reader, which does not read hover text. The dash is not a tab
    // stop: it sits inside clickable tiles and rows, and a second focus target
    // inside a button is a control nested in a control.
    return (
      <Hint content={NO_BASELINE}>
        <span
          role="img"
          aria-label={NO_BASELINE}
          className={cn("text-xs text-muted-foreground/60 tabular-nums", className)}
        >
          —
        </span>
      </Hint>
    );
  }

  const flat = Math.abs(value) < FLAT_THRESHOLD;
  const rising = value > 0;

  // Good news is a fall for cost, a rise for savings, and neither for a count.
  const good =
    sentiment === "neutral" ? null : sentiment === "higher-is-better" ? rising : !rising;

  const tone = flat
    ? "text-muted-foreground"
    : good === null
      ? "text-foreground"
      : good
        ? "text-success"
        : "text-destructive";

  const Arrow = flat ? ArrowRight : rising ? ArrowUpRight : ArrowDownRight;

  return (
    <span className={cn("inline-flex items-center gap-1 text-xs", className)}>
      <span className={cn("inline-flex items-center gap-0.5 font-medium", tone)}>
        <Arrow className="h-3 w-3" aria-hidden />
        <span className="tabular-nums">
          {flat ? "—" : `${rising ? "+" : ""}${(value * 100).toFixed(1)}%`}
        </span>
      </span>
      {caption ? <span className="text-muted-foreground">{caption}</span> : null}
    </span>
  );
}
